import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { create } from 'zustand';
import { GLOSSARY } from '../content/glossary';
import { useApp } from '../state/store';
import { MathTex } from './MathTex';
import { parseMarkup } from './Markup';

interface TermPopoverState {
  openId: string | null;
  anchor: HTMLElement | null;
  open(id: string, anchor: HTMLElement): void;
  close(): void;
}

export const useTermPopover = create<TermPopoverState>()((set) => ({
  openId: null,
  anchor: null,
  open: (id, anchor) => set({ openId: id, anchor }),
  close: () => set({ openId: null }),
}));

/**
 * Flatten nested {{term}} links for the short tooltip so we don't nest
 * interactive Term buttons inside a pointer-events:none tip. Keep $math$
 * and **bold** so parseMarkup can render them.
 */
const tipMarkup = (s: string) =>
  s
    .replace(/\{\{([^}|]+)\|([^}]+)\}\}/g, '$2')
    .replace(/\{\{([^}]+)\}\}/g, (_, id) => GLOSSARY[id]?.label ?? id);

/** First sentence of a term's meaning, with math/bold kept for tooltip rendering. */
export function shortMeaning(id: string): string {
  const entry = GLOSSARY[id];
  return entry ? tipMarkup(entry.meaning).split(/(?<=\.)\s/)[0] : '';
}

/** Shared hover-tip body: label, optional units, first-sentence meaning (with KaTeX). */
export function TermTipContent({ id }: { id: string }) {
  const entry = GLOSSARY[id];
  if (!entry) return null;
  return (
    <>
      <strong>{entry.label}</strong>
      {entry.units ? <> ({parseMarkup(entry.units, `tu-${id}`)})</> : null}: {parseMarkup(shortMeaning(id), `tm-${id}`)}
    </>
  );
}

/**
 * A glossary term: hover or focus shows a short reminder (tooltip);
 * click / Enter opens a persistent explanation. Never depends on hover or color alone.
 */
export function Term({ id, children }: { id: string; children?: ReactNode }) {
  const entry = GLOSSARY[id];
  const tipId = useId();
  const open = useTermPopover((s) => s.open);
  const setHighlight = useApp((s) => s.setHighlight);
  if (!entry) return <>{children}</>;
  const applyHoverHighlight = () => {
    if (!entry.highlight) return;
    // Don't clobber a sticky highlight set from a button.
    if (useApp.getState().highlightSticky) return;
    setHighlight(entry.highlight);
  };
  return (
    <span className="term-wrap">
      <button
        type="button"
        className="term"
        aria-describedby={tipId}
        aria-haspopup="dialog"
        onClick={(e) => {
          open(id, e.currentTarget);
          if (entry.highlight && !useApp.getState().highlightSticky) setHighlight(entry.highlight);
        }}
        onMouseEnter={applyHoverHighlight}
        onFocus={applyHoverHighlight}
      >
        {children ?? entry.label}
      </button>
      <span role="tooltip" id={tipId} className="term-tip">
        <TermTipContent id={entry.id} />
      </span>
    </span>
  );
}

/** One shared, persistent definition panel. Escape or Close returns focus to the term. */
export function TermPopover() {
  const { openId, anchor, close } = useTermPopover();
  const ref = useRef<HTMLDivElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const setHighlight = useApp((s) => s.setHighlight);
  const entry = openId ? GLOSSARY[openId] : null;
  const headingId = useId();

  useLayoutEffect(() => {
    if (!entry || !anchor) return;
    const r = anchor.getBoundingClientRect();
    const width = Math.min(360, window.innerWidth - 32);
    const left = Math.max(16, Math.min(r.left, window.innerWidth - width - 16));
    const below = r.bottom + 8;
    const top = below + 260 > window.innerHeight ? Math.max(16, r.top - 8 - 260) : below;
    setPos({ top, left });
  }, [entry, anchor]);

  const shown = !!entry && !!pos;
  useEffect(() => {
    if (!shown) return;
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        doClose();
      }
    };
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && e.target !== anchor) doClose(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, entry]);

  function doClose(returnFocus = true) {
    close();
    if (returnFocus && anchor && document.contains(anchor)) anchor.focus();
  }

  if (!entry || !pos) return null;
  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="false"
      aria-labelledby={headingId}
      className="term-popover"
      style={{ top: pos.top, left: pos.left }}
    >
      <div className="term-popover-head">
        <h2 id={headingId}>
          {entry.tex && (
            <>
              <MathTex tex={entry.tex} />{' '}
            </>
          )}
          {entry.label}
        </h2>
        <button ref={closeBtn} type="button" className="icon-btn" onClick={() => doClose()} aria-label="Close definition">
          ✕
        </button>
      </div>
      {entry.spoken && <p className="muted small">Say it: "{entry.spoken}"</p>}
      <p>{parseMarkup(entry.meaning, 'pm')}</p>
      {entry.units && (
        <p>
          <strong>Units:</strong> {parseMarkup(entry.units, 'pu')}
        </p>
      )}
      <p>
        <strong>Example:</strong> {parseMarkup(entry.example, 'pe')}
      </p>
      <div className="row gap-s">
        {entry.highlight && (
          <button
            type="button"
            className="btn small"
            onClick={() => {
              setHighlight(entry.highlight!, true);
              requestAnimationFrame(() => {
                document.querySelector(`[data-hl="${entry.highlight}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              });
            }}
          >
            Highlight in simulator
          </button>
        )}
        <a className="btn small ghost" href={`/glossary#term-${entry.id}`} onClick={() => close()}>
          Open glossary
        </a>
      </div>
    </div>
  );
}
